import { Box, Typography, Chip, Paper, Select, MenuItem, IconButton } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { PersonRemove } from "@mui/icons-material";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import type { BranchConfigurationProps } from "./BranchConfiguration.types";
import { useBranchConfigurationViewModel } from "./BranchConfiguration.vm";
import {
	GrowerCard,
	JobCard,
	EntityCardMenu,
	UnassignedGrowersPanel,
	EntitySearchInput,
	MobileAssignmentLayout,
	useMobileWizard,
	makeGrowerDraggableId,
	makeJobDraggableId,
	type EntitySortOption,
} from "../shared";
import { PortalTooltip } from "../../../../../../helpers";

function BranchesHeader({ vm }: { vm: ReturnType<typeof useBranchConfigurationViewModel> }) {
	const isSearching = vm.searchText.trim().length > 0;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
				Branches{" "}
				{isSearching ? `(${vm.filteredSortedBranches.length} / ${vm.totalBranchCount})` : `(${vm.totalBranchCount})`}
			</Typography>
			<EntitySearchInput
				value={vm.searchText}
				onChange={vm.setSearchText}
				placeholder="Search branches..."
				sx={{ ml: "auto" }}
			/>
			<Select
				size="small"
				value={vm.branchSortOption}
				onChange={(e: SelectChangeEvent) => vm.setBranchSortOption(e.target.value as EntitySortOption)}
				sx={{
					fontSize: "0.75rem",
					height: 28,
					minWidth: 0,
					"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
				}}
			>
				<MenuItem value="default" sx={{ fontSize: "0.8rem" }}>
					Default
				</MenuItem>
				<MenuItem value="name-asc" sx={{ fontSize: "0.8rem" }}>
					Name A-Z
				</MenuItem>
				<MenuItem value="name-desc" sx={{ fontSize: "0.8rem" }}>
					Name Z-A
				</MenuItem>
				<MenuItem value="growers-desc" sx={{ fontSize: "0.8rem" }}>
					Growers High→Low
				</MenuItem>
				<MenuItem value="growers-asc" sx={{ fontSize: "0.8rem" }}>
					Growers Low→High
				</MenuItem>
			</Select>
			<PortalTooltip title="Unassign all from all branches">
				<IconButton size="small" onClick={vm.unassignAll}>
					<PersonRemove fontSize="small" />
				</IconButton>
			</PortalTooltip>
		</Box>
	);
}

export function BranchConfiguration(props: BranchConfigurationProps) {
	const vm = useBranchConfigurationViewModel(props);
	const { isMobile, activeTab, setActiveTab } = useMobileWizard();

	if (isMobile) {
		return (
			<MobileAssignmentLayout
				activeTab={activeTab}
				onTabChange={setActiveTab}
				instructionText="Select growers in the Unassigned tab, then go to Assign To and tap a branch."
				unassignedGrowers={vm.unassignedGrowers}
				unassignedJobs={vm.unassignedJobs}
				growerSortOption={vm.growerSortOption}
				setGrowerSortOption={vm.setGrowerSortOption}
				selectedItems={vm.selectedItems}
				clearSelection={vm.clearSelection}
				handleItemClick={vm.handleItemClick}
				onSelectAll={() => vm.selectAllUnassigned(vm.unassignedGrowers, vm.unassignedJobs)}
				onExplodeGrower={vm.onExplodeGrower}
				onRegroupGrower={vm.onRegroupGrower}
				entities={vm.filteredSortedBranches}
				growersByEntity={vm.growersByEntity}
				jobsByEntity={vm.jobsByEntity}
				assignSelectedToEntity={vm.assignSelectedToEntity}
				assignAllToEntity={vm.assignAllToEntity}
				assignUnassignedToEntity={vm.assignUnassignedToEntity}
				unassignAllFromEntity={vm.unassignAllFromEntity}
				renderEntityHeader={(branch) => (
					<Box>
						<Typography variant="subtitle2">{branch.name}</Typography>
						{vm.regionByBranchId.get(branch.id) && (
							<Typography variant="caption" color="text.secondary">
								{vm.regionByBranchId.get(branch.id)}
							</Typography>
						)}
					</Box>
				)}
				entityHeaderBar={<BranchesHeader vm={vm} />}
				emptyEntityMessage="No branches match your search."
			/>
		);
	}

	return (
		<DragDropContext onBeforeCapture={vm.handleBeforeCapture} onDragEnd={vm.handleDragEnd}>
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary">
						Drag-and-drop growers (or individual jobs) to assign them to a branch.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 400 }}>
					<UnassignedGrowersPanel
						unassignedGrowers={vm.unassignedGrowers}
						unassignedJobs={vm.unassignedJobs}
						growerSortOption={vm.growerSortOption}
						setGrowerSortOption={vm.setGrowerSortOption}
						selectedItems={vm.selectedItems}
						clearSelection={vm.clearSelection}
						handleItemClick={vm.handleItemClick}
						draggingSelectedCount={vm.draggingSelectedCount}
						UNASSIGNED_DROPPABLE_ID={vm.UNASSIGNED_DROPPABLE_ID}
						onExplodeGrower={vm.onExplodeGrower}
						onRegroupGrower={vm.onRegroupGrower}
					/>

					{/* Right: Branch cards grid */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
						<BranchesHeader vm={vm} />
						<Box sx={{ flex: 1, overflowY: "auto" }}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
									gap: 1.5,
								}}
							>
								{vm.filteredSortedBranches.map((branch) => {
									const assignedGrowers = vm.growersByEntity.get(branch.id) || [];
									const assignedJobs = vm.jobsByEntity.get(branch.id) || [];
									return (
										<Droppable key={branch.id} droppableId={branch.id}>
											{(provided, snapshot) => (
												<Paper
													ref={provided.innerRef}
													{...provided.droppableProps}
													variant="outlined"
													sx={{
														p: 1.5,
														bgcolor: snapshot.isDraggingOver ? "primary.50" : "background.paper",
														borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
														borderWidth: snapshot.isDraggingOver ? 2 : 1,
														transition: "border-color 0.15s, background-color 0.15s",
													}}
												>
													{/* Branch header */}
													<Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
														<Box sx={{ flex: 1 }}>
															<Typography variant="subtitle2">{branch.name}</Typography>
															{vm.regionByBranchId.get(branch.id) && (
																<Typography variant="caption" color="text.secondary">
																	{vm.regionByBranchId.get(branch.id)}
																</Typography>
															)}
														</Box>
														<Chip
															label={`${assignedGrowers.length} growers`}
															size="small"
															color={assignedGrowers.length > 0 ? "primary" : "default"}
															variant="outlined"
															sx={{ mr: 0.5 }}
														/>
														<EntityCardMenu
															entityId={branch.id}
															onAssignAll={vm.assignAllToEntity}
															onAssignUnassigned={vm.assignUnassignedToEntity}
															onUnassignAll={vm.unassignAllFromEntity}
															selectedCount={vm.selectedItems.size}
															onAssignSelected={vm.assignSelectedToEntity}
														/>
													</Box>

													{/* Assigned growers & jobs */}
													<Box sx={{ minHeight: 36 }}>
														{assignedGrowers.length === 0 && assignedJobs.length === 0 && !snapshot.isDraggingOver && (
															<Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
																Drop growers/jobs here
															</Typography>
														)}
														{assignedGrowers.map((grower, index) => (
															<GrowerCard
																key={makeGrowerDraggableId(grower.name)}
																grower={grower}
																index={index}
																isSelected={vm.selectedItems.has(makeGrowerDraggableId(grower.name))}
																onToggleSelect={vm.handleItemClick}
																multiDragCount={
																	vm.selectedItems.has(makeGrowerDraggableId(grower.name))
																		? vm.draggingSelectedCount
																		: 0
																}
																onExplode={vm.onExplodeGrower}
															/>
														))}
														{assignedJobs.map((job, idx) => (
															<JobCard
																key={makeJobDraggableId(job.jobId)}
																job={job}
																index={assignedGrowers.length + idx}
																isSelected={vm.selectedItems.has(makeJobDraggableId(job.jobId))}
																onToggleSelect={vm.handleItemClick}
																multiDragCount={
																	vm.selectedItems.has(makeJobDraggableId(job.jobId)) ? vm.draggingSelectedCount : 0
																}
															/>
														))}
														{provided.placeholder}
													</Box>
												</Paper>
											)}
										</Droppable>
									);
								})}
							</Box>
							{vm.filteredSortedBranches.length === 0 && vm.totalBranchCount > 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No branches match your search.
								</Typography>
							)}
							{vm.totalBranchCount === 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No branches available.
								</Typography>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</DragDropContext>
	);
}
